/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import { getDataObject } from '../store/state/state-manager';
import type { Primitive } from '../types';
import StateMachineManager from '../page-instructions/state-machine-manager';
import { publicConstants } from '../constants';
import ContextProcessor from '../context/context-processor';
import ContextTreeManager from '../contextree/treemanager';
import Validator from '../validation/validator';
import type { NodeProps } from '../contextree/types';
import type TreeNode from '../contextree/treenode';

import Utils from './utils';
import type { EditableFormFieldOptions, FormFieldOptions } from './types';

interface FormPageField {
  fieldOrder: {
    [key: string]: string;
  };
}

interface FormPageFieldsMap {
  [key: string]: FormPageField;
}

const formPageFieldsMap: FormPageFieldsMap = {};

/**
 * Returns the property name without period if it exists
 * @param propertyName property name to be normalized
 */
export const getNormalizedPropertyName = (propertyName: string) => {
  return propertyName.startsWith('.') ? propertyName.substring(1) : propertyName;
};

/**
 * Removes the remove FormFields from the formFieldsMap when
 * container item get deleted
 * @param contextName context name to be removed from object
 */
export const removeFormFieldsFromMap = (contextName: string) => {
  delete formPageFieldsMap[contextName];
};

export const addPageFieldToForm = (contextName: string, propertyName: string, value: string) => {
  const propName = getNormalizedPropertyName(propertyName);
  if (formPageFieldsMap[contextName]) {
    formPageFieldsMap[contextName].fieldOrder[propName] = value;
  } else {
    formPageFieldsMap[contextName] = {
      fieldOrder: {
        [propName]: value
      }
    };
  }
};
const isPageListField = (contextTreeNodeProps: NodeProps) => {
  return (
    contextTreeNodeProps.selectionList ||
    contextTreeNodeProps.selectionKey ||
    (contextTreeNodeProps.renderMode === 'Editable' && contextTreeNodeProps.referenceList)
  );
};

const isFieldEditable = (contextTreeNodeProps: NodeProps, options: FormFieldOptions = {}) => {
  const { includeDisabledFields = false } = options;
  const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(contextTreeNodeProps);
  /* check readOnly, visibility, disabled */
  const isEditable =
    resolvedLiteProps.readOnly !== true &&
    resolvedLiteProps.visibility !== false &&
    (includeDisabledFields || resolvedLiteProps.disabled !== true);
  const isFilteredListField =
    typeof contextTreeNodeProps?.value === 'string' && contextTreeNodeProps?.value.includes('@FILTERED_LIST');
  const isPageListProperty = isPageListField(contextTreeNodeProps);
  return (
    /* selectionList field OR Editable referenceList */
    (contextTreeNodeProps?.value || isPageListProperty) &&
    /* FilteredList field */
    !isFilteredListField &&
    /* check  readOnly , visibility ,disabled */
    isEditable
  );
};

const pushEditableFormFieldsReference = (
  context: string,
  fieldProperty: string | undefined,
  resolvedLiteProps: object,
  key: string,
  editableFormFieldsReferenceList: string[]
) => {
  const isExecuted = StateMachineManager.executeStateMachine(
    context,
    key,
    ContextProcessor.getLitePropertyValue(resolvedLiteProps, fieldProperty)
  );

  if (!isExecuted) {
    editableFormFieldsReferenceList.push(key);
  }
  return editableFormFieldsReferenceList;
};

const isScalarField = (key: string, resolvedLiteProps: NodeProps, options: EditableFormFieldOptions) => {
  const nonScalarComponents = ['Address', 'Attachment'];
  const isInsideList = resolvedLiteProps.isInsideList;
  const { ignoreFieldsInsideList = true } = options;
  return (
    (!isInsideList || (isInsideList && !ignoreFieldsInsideList)) &&
    !Array.isArray(ContextProcessor.getLitePropertyValue(resolvedLiteProps, key)) &&
    !(resolvedLiteProps.componentName && nonScalarComponents.includes(resolvedLiteProps.componentName))
  );
};

const getFormFieldsList = (
  formFieldsMap: Map<string, TreeNode>,
  context: string,
  options: EditableFormFieldOptions
) => {
  const { includeDisabledFields = false } = options;
  let editableFormFieldsReferenceList: string[] = [];
  for (const [key, fieldNode] of formFieldsMap) {
    const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(fieldNode.props);
    const fieldProperty = resolvedLiteProps.propertyName as string;
    if (isFieldEditable(fieldNode.props, { includeDisabledFields }) && isScalarField(key, resolvedLiteProps, options)) {
      editableFormFieldsReferenceList = pushEditableFormFieldsReference(
        context,
        fieldProperty,
        resolvedLiteProps,
        key,
        editableFormFieldsReferenceList
      );
    }
  }
  return {
    editableFormFieldsReferenceList
  };
};

export const getEditableFormFieldsReferenceList = (context: string, options: EditableFormFieldOptions = {}) => {
  let editableFormFieldsReferenceList: string[] = [];
  const formPageFieldsList: string[] = [];
  const formFieldsMap = ContextTreeManager.getContextReferences(context);
  if (!formFieldsMap) {
    return {
      formInputs: editableFormFieldsReferenceList,
      formPageFields: formPageFieldsList
    };
  }
  ({ editableFormFieldsReferenceList } = getFormFieldsList(formFieldsMap, context, options));
  if (formPageFieldsMap[context]) {
    formPageFieldsList.push(...Object.keys(formPageFieldsMap[context].fieldOrder ?? {}));
  }
  return {
    formInputs: editableFormFieldsReferenceList,
    formPageFields: formPageFieldsList
  };
};

export const getAttachmentsList = (context: string) => {
  let attachmentList: object[] = [];
  const formFieldMap = ContextTreeManager.getContextReferences(context);
  if (formFieldMap) {
    for (const [key, fieldNode] of formFieldMap) {
      const fieldProps = fieldNode?.props;
      if (isFieldEditable(fieldProps) && fieldProps?.componentName === 'Attachment') {
        const allowMultiple = fieldProps?.allowMultiple === 'true';
        attachmentList = [...attachmentList, { type: 'File', allowMultiple, fullPath: key }];
      }
    }
  }
  return attachmentList;
};
const buildSubmitPayload = (
  formPageFields: string[],
  data: { [key: string]: string | number | boolean },
  resultObj: { [key: string]: string | number | boolean }
) => {
  formPageFields.forEach((propRef) => {
    let valueObj = Utils.getIn(data, propRef);

    if (typeof valueObj === 'object') {
      // deepClone the object to avoid Readonly object errors when setting properties inside the object.
      valueObj = Utils.deepClone(valueObj);
      delete valueObj?.classID;
    }
    Utils.buildObject(propRef, resultObj, valueObj);
  });
};

export const getDataBasedOnWorkType = (
  isObjectWideAction: boolean,
  isExternalObject: boolean,
  data: { [key: string]: Primitive }
) => (isObjectWideAction || isExternalObject ? data?.objectInfo : data?.caseInfo);

/**
 * this function provides the values for the formfields present in the given context
 * @param context - context of form
 * @param options parameters like ignoreFieldsInsideList
 * @returns - contentObject
 */
export const getFormContent = (context: string, options: EditableFormFieldOptions = {}) => {
  const resultObj = {};
  const data = getDataObject(context);
  const {
    ignoreFieldsInsideList = true,
    isTransientContext = false,
    includeDisabledFields = false,
    isObjectWideAction = false,
    isExternalObject = false
  } = options;
  const info = getDataBasedOnWorkType(isObjectWideAction, isExternalObject, data);
  if (isTransientContext || info) {
    const { formInputs, formPageFields } = getEditableFormFieldsReferenceList(context, {
      ignoreFieldsInsideList,
      includeDisabledFields
    });

    buildSubmitPayload(formPageFields, data, resultObj);

    if (formInputs && formInputs.length > 0) {
      formInputs.forEach((propRef) => {
        Utils.buildObject(propRef, resultObj, Utils.getIn(data, propRef));
      });
    } else if (!isTransientContext) {
      Utils.buildObject(publicConstants.CASE_INFO.CASE_INFO_CONTENT, resultObj, {});
    } else {
      // not reachable
    }
  }
  if (!isTransientContext && data?.dataInfo?.content) {
    const { formInputs } = getEditableFormFieldsReferenceList(context, { ignoreFieldsInsideList });
    formInputs.forEach((propRef) => {
      Utils.buildObject(propRef, resultObj, Utils.getIn(data, propRef));
    });
  }
  return resultObj;
};

export const getResolvedContextTreeNodeProps = (context: string, pageReference: string, propertyName?: string) => {
  const propertyReference =
    pageReference === '' && propertyName?.startsWith('.')
      ? propertyName.substring(1, propertyName.length)
      : propertyName;
  const props = ContextTreeManager.getContextReferences(context)?.get(`${pageReference}${propertyReference}`)?.props;
  if (props) {
    return ContextProcessor.resolveContextTreeNodeProps(props);
  }
  return props;
};

/**
 * Checks whether form is valid or not
 * based on the validation values for form fields
 * @param context context of form fields to be validated
 * @returns boolean
 */
export const isFormValid = (context: string | null, pageReference?: string | null) => {
  const formFieldMap = ContextTreeManager.getContextReferences(context);
  let isValid = true;

  if (!formFieldMap) {
    return isValid;
  }
  for (const [key, fieldNode] of formFieldMap) {
    const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(fieldNode.props);
    /* istanbul ignore next */
    if (isFieldEditable(fieldNode.props) && !(pageReference && !key.includes(pageReference))) {
      const messageObject = Validator.validate(
        resolvedLiteProps.componentName?.toLowerCase(),
        resolvedLiteProps.propertyName,
        ContextProcessor.getLitePropertyValue(resolvedLiteProps, resolvedLiteProps.propertyName),
        resolvedLiteProps
      );
      if (isValid) {
        isValid = !messageObject.message;
      }
    }
  }
  return isValid;
};

/**
 * Checks whether form has active suggestion
 * @private
 * @param context context of form fields
 * @returns boolean
 */
export const isFormHasSuggestions = (context: string | null) => {
  const formFieldMap = ContextTreeManager.getContextReferences(context);
  if (formFieldMap) {
    for (const [, fieldNode] of formFieldMap) {
      const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(fieldNode.props);

      if (resolvedLiteProps.hasSuggestions) {
        return true;
      }
    }
  }
  return false;
};

/**
 * returns all the fields in the form which are visible
 * @param context context
 */
export const getVisibleFormFields = (context: string, includeFullPath: boolean = false) => {
  const formFieldMap = ContextTreeManager.getContextReferences(context);
  const fields: string[] = [];
  if (formFieldMap) {
    for (const [key, fieldNode] of formFieldMap) {
      const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(fieldNode.props);

      if (resolvedLiteProps.visibility !== false) {
        const startIndex = 8;
        const modifiedKey = includeFullPath ? key : key.substring(key.indexOf('content') + startIndex);
        fields.push(modifiedKey);
      }
    }
  }
  return fields;
};

/**
 * returns all the fields in the form which are readonly
 * @param context context
 */
export const getReadOnlyFormFields = (context: string) => {
  const formFieldMap = ContextTreeManager.getContextReferences(context);
  const fields: string[] = [];
  if (formFieldMap) {
    for (const [key, fieldNode] of formFieldMap) {
      const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(fieldNode.props);

      if (resolvedLiteProps.readOnly === true) {
        fields.push(key);
      }
    }
  }
  return fields;
};

/**
 * returns all the fields in the form which are editable
 * @param context context
 * @returns editable fields information in an array -
 [{
 "name": "<field full path>",
 "label": "<field label>",
 "type": "<field type>"
 }]
 */
export const getEditableFields = (context: string) => {
  const editableFormFields: { name: string; label?: string; type?: string }[] = [];
  const formFieldMap = ContextTreeManager.getContextReferences(context);
  if (formFieldMap) {
    for (const [key, fieldNode] of formFieldMap) {
      const resolvedLiteProps: NodeProps = ContextProcessor.resolveContextTreeNodeProps(fieldNode.props);
      if (
        isFieldEditable(fieldNode.props) &&
        resolvedLiteProps.componentName !== 'Attachment' &&
        resolvedLiteProps.type !== 'multirecordlist'
      ) {
        editableFormFields.push({
          name: key,
          label: resolvedLiteProps.label,
          type: resolvedLiteProps.componentName?.toLowerCase()
        });
      }
    }
  }
  return editableFormFields;
};
